/**
 * Os atalhos do painel para as tarefas mais comuns.
 *
 * Cada um pergunta o que a tela de destino exige antes de se oferecer — issue #440. Antes
 * disso todos apareciam para todo mundo, e quem não alcançava a tela descobria no clique, numa
 * página de "Acesso Negado" que não diz o que fazer a respeito.
 *
 * A pergunta é a mesma do resto da tela — `avaliar`, com as quatro perguntas do contexto ativo.
 * O que muda aqui é o que se faz com a resposta: dentro da tela do assunto o controle bloqueado
 * fica à vista para ensinar que a função existe, mas no painel inicial o atalho é uma sugestão
 * do que fazer agora, e sugestão que não dá para seguir só ocupa o lugar de quem acabou de
 * entrar. Fora do contexto ativo, ele não aparece (#443).
 *
 * @version 2025.2.0
 */
import React from 'react';
import { Card, CardContent, Typography, Grid } from '@mui/material';
import {
  EventNote,
  PersonAdd,
  School,
  LocalHospital,
  FlashOn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { RouteActionButton } from '../../../components';
import { avaliar, pode } from '../../../authorization/pode';
import { useManifesto } from '../../../stores/manifestoStore';
import { QuickActionsProps, QuickActionItem } from '../../../types/dashboard';

/**
 * Default quick actions
 *
 * O que cada atalho exige é o que a rota dele exige em `App.tsx`.
 */
const defaultActions: QuickActionItem[] = [
  {
    id: 'schedule-exam',
    label: 'Agendar Exame',
    icon: <EventNote />,
    path: '/appointments/new',
    exige: pode('appointment.create'),
    color: 'primary',
  },
  {
    id: 'add-employee',
    label: 'Novo Funcionário',
    icon: <PersonAdd />,
    path: '/employees/new',
    exige: pode('employee.create'),
    color: 'success',
  },
  {
    id: 'add-training',
    label: 'Novo Treinamento',
    icon: <School />,
    path: '/trainings/new',
    exige: pode('training.create'),
    color: 'warning',
  },
  {
    id: 'reception',
    label: 'Recepção',
    icon: <LocalHospital />,
    path: '/reception',
    exige: pode('reception.read'),
    color: 'info',
  },
];

/**
 * Single action button component
 */
const ActionButton: React.FC<{
  action: QuickActionItem;
  onClick: () => void;
}> = ({ action, onClick }) => {
  return (
    <RouteActionButton
      para={action.path}
      exige={action.exige}
      aoAgir={onClick}
      variant="outlined"
      color={action.color || 'primary'}
      startIcon={action.icon}
      sx={{
        width: '100%',
        py: 1.5,
        justifyContent: 'flex-start',
        textTransform: 'none',
        fontWeight: 500,
        borderWidth: 1.5,
        '&:hover': {
          borderWidth: 1.5,
          transform: 'translateY(-1px)',
          boxShadow: 1,
        },
      }}
    >
      {action.label}
    </RouteActionButton>
  );
};

const QuickActions: React.FC<QuickActionsProps> = ({ actions, onActionClick }) => {
  const navigate = useNavigate();
  const { manifesto } = useManifesto();
  const displayActions = actions || defaultActions;

  // Sem manifesto ninguém sabe de nada, e nada se oferece — a mesma regra de `useVeredito`.
  const permitidas = manifesto
    ? displayActions.filter((action) => avaliar(action.path, manifesto, action.exige).liberado)
    : [];

  const handleActionClick = (action: QuickActionItem) => {
    if (onActionClick) {
      onActionClick(action);
    } else {
      navigate(action.path);
    }
  };

  // Nenhum atalho a operar não é um cartão vazio: o bloco inteiro sai da tela.
  if (permitidas.length === 0) return null;

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
        >
          <FlashOn color="primary" />
          Ações Rápidas
        </Typography>

        <Grid container spacing={2}>
          {permitidas.map((action) => (
            <Grid
              key={action.id}
              size={{
                xs: 12,
                sm: 6,
                md: 3,
              }}
            >
              <ActionButton action={action} onClick={() => handleActionClick(action)} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default QuickActions;
